import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { LeaveApplication, LeaveType } from '@chronos/shared';
import { useAuth } from '@/auth/AuthContext';
import { useLeaveApplications, useLeaveMutations, useUsers } from '@/hooks/queries';
import { Card, EmptyState, ErrorNote, PageHeader, Spinner, StatTile, SubmitButton } from '@/components/ui';
import { formatDate, formatDateTime } from '@/lib/format';
import { FiledByBadge, StatusBadge, TypeBadge } from '@/pages/LeavePage';

/**
 * Admins approve or reject casual leave here; managers see their team's
 * requests read-only. Medical leave shows up already approved.
 */
export function LeaveRequestsPage() {
  const { hasRole } = useAuth();
  const canDecide = hasRole('ADMIN');
  const pending = useLeaveApplications({ team: true, status: 'PENDING' });
  const all = useLeaveApplications({ team: true });
  const { decide } = useLeaveMutations();
  const [note, setNote] = useState<Record<string, string>>({});

  const history = (all.data ?? []).filter((a) => a.status !== 'PENDING').slice(0, 100);

  const act = (a: LeaveApplication, status: 'APPROVED' | 'REJECTED') =>
    decide.mutate({ id: a.id, status, note: note[a.id]?.trim() || null }, { onSuccess: () => setNote((n) => ({ ...n, [a.id]: '' })) });

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Leave requests"
        description={canDecide ? 'Casual leave waits here for your decision. Medical leave is approved automatically and listed for the record.' : 'Requests from the people on projects you manage. Approval is done by an admin.'}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="Awaiting approval" value={String(pending.data?.length ?? 0)} tone={pending.data?.length ? 'brand' : 'default'} />
        <StatTile label="Approved (all time shown)" value={String((all.data ?? []).filter((a) => a.status === 'APPROVED').length)} tone="green" />
        <StatTile label="Rejected" value={String((all.data ?? []).filter((a) => a.status === 'REJECTED').length)} />
      </div>

      <ErrorNote error={pending.error ?? all.error ?? decide.error} />

      {canDecide && <FileForEmployeeCard />}

      <Card className="mt-4">
        <div className="border-b border-slate-200 px-4 py-3"><h2 className="text-sm font-semibold text-slate-800">Awaiting approval</h2></div>
        {pending.isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (pending.data?.length ?? 0) === 0 ? (
          <EmptyState title="Nothing to decide" description="New casual-leave requests appear here." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {pending.data!.map((a) => (
              <li key={a.id} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <TypeBadge type={a.type} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-800">
                      <Link to={`/attendance?userId=${a.userId}&month=${a.fromDate.slice(0, 7)}`} className="font-medium text-brand-700 hover:underline">{a.userName}</Link>
                      {' · '}{formatDate(`${a.fromDate}T00:00:00`)}{a.toDate !== a.fromDate ? ` – ${formatDate(`${a.toDate}T00:00:00`)}` : ''}
                      <span className="ml-2 text-xs text-slate-500">{a.units} day{a.units === 1 ? '' : 's'}{a.halfDay ? ' (half)' : ''}</span>
                      {a.filedByName && <span className="ml-2"><FiledByBadge name={a.filedByName} /></span>}
                    </p>
                    <p className="text-xs text-slate-500">{a.reason} · applied {formatDateTime(a.createdAt)}</p>
                  </div>
                  {canDecide ? (
                    <div className="flex items-center gap-2">
                      <input className="input max-w-[14rem] py-1 text-xs" placeholder="Note (optional)" value={note[a.id] ?? ''} onChange={(e) => setNote((n) => ({ ...n, [a.id]: e.target.value }))} />
                      <button className="btn-secondary py-1 text-xs text-rose-700" disabled={decide.isPending} onClick={() => act(a, 'REJECTED')}>Reject</button>
                      <button className="btn-primary py-1 text-xs" disabled={decide.isPending} onClick={() => act(a, 'APPROVED')}>Approve</button>
                    </div>
                  ) : (
                    <StatusBadge status={a.status} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="mt-4">
        <div className="border-b border-slate-200 px-4 py-3"><h2 className="text-sm font-semibold text-slate-800">Decided and auto-approved</h2></div>
        {all.isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : history.length === 0 ? (
          <EmptyState title="No history yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="th">Person</th>
                  <th className="th">Type</th>
                  <th className="th">Dates</th>
                  <th className="th text-right">Days</th>
                  <th className="th">Reason</th>
                  <th className="th">Status</th>
                  <th className="th">Decided</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="td font-medium">
                      <Link to={`/attendance?userId=${a.userId}&month=${a.fromDate.slice(0, 7)}`} className="text-brand-700 hover:underline">{a.userName}</Link>
                    </td>
                    <td className="td">
                      <div className="flex flex-wrap items-center gap-1">
                        <TypeBadge type={a.type} />
                        <FiledByBadge name={a.filedByName} />
                      </div>
                    </td>
                    <td className="td whitespace-nowrap">{formatDate(`${a.fromDate}T00:00:00`)}{a.toDate !== a.fromDate ? ` – ${formatDate(`${a.toDate}T00:00:00`)}` : ''}</td>
                    <td className="td text-right tabular-nums">{a.units}</td>
                    <td className="td max-w-xs truncate text-slate-600" title={a.reason}>{a.reason}</td>
                    <td className="td"><StatusBadge status={a.status} />{a.decisionNote && a.decisionNote !== 'Auto-approved (medical)' && a.decisionNote !== 'Filed and approved by admin' && <p className="mt-0.5 text-[11px] text-slate-500">{a.decisionNote}</p>}</td>
                    <td className="td whitespace-nowrap text-xs text-slate-500">{a.decidedAt ? `${formatDateTime(a.decidedAt)}${a.decidedByName ? ` · ${a.decidedByName}` : ''}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Admin-only: record leave for an employee, typically to convert an absence
 * the system marked as unapproved into bank-paid leave. Approved on creation,
 * and tagged so the sheet shows who filed it.
 */
function FileForEmployeeCard() {
  const users = useUsers({ status: 'ACTIVE' });
  const { apply } = useLeaveMutations();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [type, setType] = useState<LeaveType>('CASUAL');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const reset = () => { setUserId(''); setFrom(''); setTo(''); setHalfDay(false); setReason(''); };

  return (
    <Card className="mt-4">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">File leave for an employee</h2>
          <p className="text-xs text-slate-500">Backdate an absence so it draws from the leave bank instead of being deducted from salary.</p>
        </div>
        <button className="btn-secondary py-1 text-xs" onClick={() => { setOpen((o) => !o); setDone(null); }}>{open ? 'Close' : 'File leave'}</button>
      </div>

      {open && (
        <form
          className="flex flex-wrap items-end gap-3 px-4 py-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!userId || !from || !reason.trim()) return;
            apply.mutate(
              { userId, type, from, to: halfDay ? from : (to || from), halfDay, reason: reason.trim() },
              { onSuccess: (a) => { setDone(`Recorded ${a.units} day${a.units === 1 ? '' : 's'} for ${a.userName}.`); reset(); } },
            );
          }}
        >
          <div className="min-w-[12rem] flex-1">
            <label className="label" htmlFor="fb-user">Employee</label>
            <select id="fb-user" className="input" required value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Select…</option>
              {(users.data?.items ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="fb-type">Type</label>
            <select id="fb-type" className="input" value={type} onChange={(e) => setType(e.target.value as LeaveType)}>
              <option value="CASUAL">Casual</option>
              <option value="MEDICAL">Medical</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="fb-from">From</label>
            <input id="fb-from" type="date" className="input" required value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="fb-to">To</label>
            <input id="fb-to" type="date" className="input" value={halfDay ? from : to} min={from} disabled={halfDay} onChange={(e) => setTo(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
            <input type="checkbox" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)} /> Half day
          </label>
          <div className="min-w-[14rem] flex-1">
            <label className="label" htmlFor="fb-reason">Reason</label>
            <input id="fb-reason" className="input" required maxLength={500} placeholder="e.g. Was unwell, informed by phone" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <SubmitButton pending={apply.isPending} className="btn-primary">File &amp; approve</SubmitButton>
        </form>
      )}

      {open && <div className="px-4 pb-3"><ErrorNote error={apply.error} />{done && <p className="text-xs text-emerald-700">{done}</p>}</div>}
    </Card>
  );
}
